import {
  Checkbox,
  DefaultButton,
  Label,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Slider,
  Spinner,
  TextField,
  TooltipHost,
} from "@fluentui/react";
import { useEffect, useState } from "react";
import { AppConfig } from "../../Config";
import { ISensor } from "../../Models/ISensor";
import { ISentinel, ISentinelRich } from "../../Models/ISentinel";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { getSensorIconTooltip } from "../../Utils/SensorMaps";
import { ToastMessage } from "../../Utils/UIMessages";
import IconButtonItem from "../IconButtonItem/IconButtonItem";
import "./ConfigureCard.scss";
import { useSelector } from "react-redux";
import { GlobalState } from "../../Redux/RootReducer";
import { IRole } from "../../Models/IRole";
import { JSONPrint } from "../../Utils/Generic";
import { developerUI } from "../../Pages/OptionsPage/OptionsPage";
import SmartModal, {
  DismissModal,
  SummonModal,
} from "../SmartModal/SmartModal";
import SentinelTools from "../SentinelTools/SentinelTools";
import {
  isSensorOffline,
  getOfflineTooltipContent,
} from "../../Utils/SensorStatus";

export interface IConfigureCardProps {
  sentinel: ISentinelRich;
  onChange: (sentinel: ISentinelRich) => void;
}

export interface ISensorReading {
  sensorId: number;
  lastValue: string;
  threshold?: string | number | null; // porta: livello batteria (0..3) aggiornato dal keepalive
  updatedAt: string;
}

const ConfigureCard = (props: IConfigureCardProps) => {
  const [refSentinel, setRefSentinel] = useState<ISentinelRich>();
  const [rerender, setRerender] = useState<boolean>(false);
  const [editedSentinel, setEditedSentinel] = useState<boolean>(false);
  const [editedSentinelSave, setEditedSentinelSave] = useState<boolean>(false);
  const [realTimeValues, setRealTimeValues] = useState<ISensorReading[]>([]);
  const [realTimeLoadError, setRealTimeLoadError] = useState<boolean>(false);
  const [, setTick] = useState<number>(0);

  const [realTimeValuesPollInterval, setRealTimeValuesPollInterval] =
    useState<NodeJS.Timer | null>(null);
  const loggedUser = useSelector((s: GlobalState) => s.generic.user);

  // Timer per forzare re-render periodico e aggiornare lo stato offline
  useEffect(() => {
    const checkIntervalMs = AppConfig.sensor.offlineCheckInterval * 1000;
    
    const interval = setInterval(() => {
      setTick((prev: number) => prev + 1);
    }, checkIntervalMs);
    
    return () => clearInterval(interval);
  }, []);

  const emit = () => {
    if (refSentinel) {
      props.onChange(refSentinel);
    }
  };

  const saveAllSensors = () => {
    if (refSentinel) {
      refSentinel.sensors.forEach((x: ISensor, i: number) => {
        saveSensor(i, true);
      });
    }
  };

  const saveSentinel = () => {
    saveAllSensors();
    if (refSentinel) {
      setEditedSentinel(false);
      setEditedSentinelSave(true);
      ApiService.PatientController.UpdatePatient(
        refSentinel.patient,
        (response: IAPIResponse) => {
          if (response.error === null) {
            ToastMessage("success", "Ospite aggiornato con successo.");
          } else {
            ToastMessage("error", "Impossibile aggiornare l'ospite.");
            setEditedSentinel(true);
          }
          setEditedSentinelSave(false);
        }
      );
    }
  };

  const saveSensorApi = (
    sentinelId: number,
    sensor: ISensor,
    skipSuccessMessage = false
  ) => {
    ApiService.SentinelController.UpdateSensor(
      sentinelId.toString(),
      sensor,
      (response: IAPIResponse) => {
        if (response.error == null) {
          if (!skipSuccessMessage) {
            ToastMessage("success", "Tara aggiornata.");
          }
        } else {
          ToastMessage("error", "Non è stato possibile aggiornare il sensore.");
        }
      }
    );
  };

  const saveSensor = (i: number, silent = false) => {
    if (refSentinel) {
      let target: ISensor = refSentinel.sensors[i];
      if (target.type === "pesogr") {
        target.threshold = target.startThreshold * 1.5;
      }

      ApiService.SentinelController.UpdateSensor(
        refSentinel.id.toString(),
        target,
        (response: IAPIResponse) => {
          if (response.error == null) {
            // update pesog2 as pesogr
            if (target.type === "pesogr") {
              let pesogr2: ISensor | undefined = refSentinel.sensors.find(
                (x: ISensor) => x.type === "pesogr2"
              );

              if (pesogr2) {
                saveSensorApi(
                  refSentinel.id,
                  {
                    ...target,
                    startThreshold: pesogr2.startThreshold,
                    threshold: pesogr2.startThreshold * 1.5,
                    type: "pesogr2",
                    id: pesogr2.id,
                  },
                  true
                );
              }
            }

            if (!silent) {
              ToastMessage("success", "Sensore aggiornato.");
            }
          } else {
            ToastMessage(
              "error",
              "Non è stato possibile aggiornare il sensore."
            );
          }
        }
      );
    }
  };

  const getRealTimeValue = (sensor: ISensor) => {
    let value: number = 0;
    let matches: ISensorReading[] = realTimeValues.filter(
      (x: ISensorReading) => x.sensorId == sensor.id
    );
    if (matches.length > 0) {
      value = +matches[0].lastValue;
    }
    value = value ?? 0;
    value = Math.floor(value);
    return value > 0 ? value : 0;
  };

  const pollRealTimeValues = (sentinelId: number) => {
    setTimeout(() => {
      if (
        window.document.getElementsByClassName(
          "configure-card-is-on-" + sentinelId.toString()
        ).length > 0
      ) {
        ApiService.SentinelController.GetRealTimeSensorValues(
          sentinelId.toString(),
          (response: IAPIResponse) => {
            if (response.error == null) {
              setRealTimeValues(response.parsed);
              setRealTimeLoadError(false);
            } else {
              setRealTimeLoadError(true);
            }
          }
        );
        setTimeout(() => {
          pollRealTimeValues(sentinelId);
        }, 500);
      }
    }, 500);
  };

  useEffect(() => {
    emit();
    // break links clone
    setRefSentinel(JSON.parse(JSON.stringify(props.sentinel)));
    let falseArray: boolean[] = props.sentinel.sensors.map((x: ISensor) => {
      return false;
    });
    pollRealTimeValues(props.sentinel.id);
    setEditedSentinel(false);
    setRealTimeLoadError(false);
  }, [props.sentinel.id]);

  // Aggiorna refSentinel quando props.sentinel cambia (per aggiornare updatedAt)
  useEffect(() => {
    if (refSentinel && !editedSentinel) {
      setRefSentinel(JSON.parse(JSON.stringify(props.sentinel)));
    }
  }, [props.sentinel]);

  useEffect(() => {
    return () => {
      if (realTimeValuesPollInterval) {
        clearInterval(realTimeValuesPollInterval);
      }
    };
  }, []);

  const renderNormalSensor = (refSentinel: ISentinelRich, x: ISensor) => {
    let isPesoSensor = x.type.includes("peso");
    let isDoorSensor = x.type === "door";
    return (
      <div className="configure-card-slider-zone">
        {(isPesoSensor || isDoorSensor) && (
          <span className="cf-np">Il sensore non richiede parametrizzazione</span>
        )}
        {!isPesoSensor && !isDoorSensor && (
          <div>
            <div className="slider-on-row-label-wrap">
              <div className="slider-on-row-label-wrap-label">
                Imposta valore
              </div>
              <div className="slider-on-row-label-wrap-slider">
                <Slider
                  min={x.startThreshold}
                  max={x.endThreshold}
                  step={1}
                  value={x.threshold}
                  showValue
                  snapToStep
                  disabled={!refSentinel.enabled}
                  onChange={(v: number) => {
                    let sentinel: ISentinelRich = {
                      ...refSentinel,
                    };
                    let targetIndex = sentinel.sensors.findIndex(
                      (s: ISensor) => s.id === x.id
                    );
                    sentinel.sensors[targetIndex].threshold = v;
                    setRefSentinel(sentinel);
                    setEditedSentinel(true);
                  }}
                />
              </div>
            </div>
            <div className="slider-on-row-label-wrap">
              <div className="slider-on-row-label-wrap-label">
                Rilevato Real-Time
              </div>
              <div className="slider-on-row-label-wrap-slider">
                <Slider
                  min={x.startThreshold}
                  max={x.endThreshold}
                  step={1}
                  value={getRealTimeValue(x)}
                  showValue
                  snapToStep
                />
              </div>
            </div>
            <div className="strict-flex-line">
              <TextField
                label={"Limite minore"}
                onChange={(e, t) => {
                  if (t) {
                    let sentinel: ISentinelRich = {
                      ...refSentinel,
                    };
                    let targetIndex = sentinel.sensors.findIndex(
                      (s: ISensor) => s.id === x.id
                    );
                    sentinel.sensors[targetIndex].startThreshold = +t;
                    setRefSentinel(sentinel);
                    setEditedSentinel(true);
                  }
                }}
                defaultValue={x.startThreshold.toString()}
                placeholder="Limite minore"
              />
              <TextField
                label="Limite maggiore"
                onChange={(e, t) => {
                  if (t) {
                    let sentinel: ISentinelRich = {
                      ...refSentinel,
                    };
                    let targetIndex = sentinel.sensors.findIndex(
                      (s: ISensor) => s.id === x.id
                    );
                    sentinel.sensors[targetIndex].endThreshold = +t;
                    setRefSentinel(sentinel);
                    setEditedSentinel(true);
                  }
                }}
                defaultValue={x.endThreshold.toString()}
                placeholder="Limite maggiore"
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const getWeightPatient = () => {
    let peso_1: ISensor | undefined = refSentinel?.sensors.find(
      (x: ISensor) => x.type === "pesogr"
    );
    let peso_2: ISensor | undefined = refSentinel?.sensors.find(
      (x: ISensor) => x.type === "pesogr2"
    );

    let tara_peso_1 = peso_1?.startThreshold ?? 0;
    let tara_peso_2 = peso_2?.startThreshold ?? 0;
    let taraTotale = tara_peso_1 + tara_peso_2;
    if (peso_1 && peso_2) {
      let real_time_p1 = getRealTimeValue(peso_1);
      let real_time_p2 = getRealTimeValue(peso_2);
      let finalWeight = (real_time_p1 + real_time_p2 - taraTotale) / 10;

      //return <span>{w > 0 ? w / 10 : 0}</span>;

      return {
        tara1: tara_peso_1,
        tara2: tara_peso_2,
        p1: real_time_p1 / 10,
        p2: real_time_p2 / 10,
        displayWeight: finalWeight,
      };
    }
    return null;
  };

  const weightInfo = getWeightPatient();

  return (
    <div>
      <SmartModal
        title="Strumenti del Letto"
        modalUniqueId="tools-sensor-config-page"
        content={
          <SentinelTools
            realTimeValues={realTimeValues}
            sentinel={refSentinel}
          />
        }
        buttons={[
          {
            text: "Chiudi",
            onClick: () => {
              DismissModal("tools-sensor-config-page");
            },
          },
        ]}
      />
      {refSentinel !== undefined && (
        <div className={"configure-card-is-on-" + refSentinel.id.toString()}>
          <form
            onSubmit={(e: any) => {
              e.preventDefault();
              saveSentinel();
            }}
          >
            <div className="cf-title">
              <div>
                <span className="t">Configura {props.sentinel.identifier}</span>
                <small>
                  ID: {props.sentinel.id}
                  {props.sentinel.patient && props.sentinel.patient.identifier && props.sentinel.patient.identifier !== "-"
                    ? " · " + props.sentinel.patient.identifier
                    : ""}
                </small>
              </div>
              {!editedSentinelSave && (
                // spento resta leggibile e dice perché
                <div className="cf-save">
                  <PrimaryButton disabled={!editedSentinel} iconProps={{ iconName: "Save" }} text="Salva" type="submit" onClick={() => {}} />
                  {!editedSentinel && <span className="cf-save-why">Nessuna modifica da salvare</span>}
                </div>
              )}
              {editedSentinelSave && <Spinner />}
            </div>
            {!refSentinel.enabled && (
              <MessageBar messageBarType={MessageBarType.warning}>
                <strong>
                  Attenzione: il Letto è disabilitato. Non è possibile
                  modificarne i parametri.
                </strong>
              </MessageBar>
            )}
            {realTimeLoadError && (
              <MessageBar messageBarType={MessageBarType.error}>
                Errore: non è possibile caricare i dati Real-Time per questa
                Letto. Verificare che il Letto sia acceso.
              </MessageBar>
            )}
            <div>
              <div className="cf-div">Persona assistita</div>
              {!refSentinel?.enabled && (
                <div className="readonly-data">
                  <div className="readonly-data-block">
                    <div className="readonly-data-titile">Identificativo</div>
                    <div className="readonly-data-value">
                      {refSentinel.patient.identifier}
                    </div>
                  </div>
                  <div className="readonly-data-block">
                    <div className="readonly-data-titile">Zona</div>
                    <div className="readonly-data-value">
                      {refSentinel.patient.floor}
                    </div>
                  </div>
                  <div className="readonly-data-block">
                    <div className="readonly-data-titile">Stanza</div>
                    <div className="readonly-data-value">
                      {refSentinel.patient.room}
                    </div>
                  </div>
                </div>
              )}
              {refSentinel?.enabled && (
                <div className="cf-fields">
                <div className="inline-block-configure-input">
                  <TextField
                    label="Identificativo"
                    placeholder="Identificativo"
                    required
                    disabled={!refSentinel.enabled || editedSentinelSave}
                    onChange={(e: any, t: string | undefined) => {
                      if (t !== undefined) {
                        let s: ISentinelRich = { ...refSentinel };
                        s.patient.identifier = t;
                        setRefSentinel(s);
                        setEditedSentinel(true);
                      }
                    }}
                    value={refSentinel.patient.identifier}
                  />
                </div>
                <div className="inline-block-configure-input">
                  <TextField
                    label="Zona"
                    placeholder="Zona"
                    required
                    disabled={!refSentinel.enabled || editedSentinelSave}
                    type="text"
                    onChange={(e: any, t: string | undefined) => {
                      if (t !== undefined) {
                        let s: ISentinelRich = { ...refSentinel };
                        s.patient.floor = t;
                        setRefSentinel(s);
                        setEditedSentinel(true);
                      }
                    }}
                    value={refSentinel.patient.floor}
                  />
                </div>
                <div className="inline-block-configure-input">
                  <TextField
                    label="Stanza"
                    placeholder="Stanza"
                    required
                    disabled={!refSentinel.enabled || editedSentinelSave}
                    type="number"
                    onChange={(e: any, t: string | undefined) => {
                      if (t !== undefined) {
                        let s: ISentinelRich = { ...refSentinel };
                        s.patient.room = t;
                        setRefSentinel(s);
                        setEditedSentinel(true);
                      }
                    }}
                    value={refSentinel.patient.room}
                  />
                </div>
                </div>
              )}
              {false && (
                <div className="inline-block-configure-input fill-width-on-small-screen">
                  <TextField
                    label="Letto"
                    placeholder="Letto"
                    required
                    disabled={refSentinel?.enabled || editedSentinelSave}
                    type="number"
                    onChange={(e: any, t: string | undefined) => {
                      /*
                      if (t !== undefined) {
                        let s: ISentinelRich = { ...refSentinel };
                        s.patient.bed = t;
                        setRefSentinel(s);
                        setEditedSentinel(true);
                      }*/
                    }}
                    value={refSentinel?.patient.bed}
                  />
                </div>
              )}
            </div>
          </form>
          {weightInfo && (
            <div>
              <div className="horiz-separator"></div>
              <div>
                Peso del paziente:{" "}
                <strong>
                  {weightInfo.displayWeight > 0 ? weightInfo.displayWeight : 0}{" "}
                  Kg
                </strong>
              </div>
            </div>
          )}
          <div className="horiz-separator"></div>
          <div>
            <SmartModal
              title="Calibrazione Tara"
              modalUniqueId="configureCard-tara-modal"
              content={
                <div>
                  <h2>Istruzioni per il calcolo della tara</h2>
                  <div>
                    Il calcolo della tara è un passaggio necessario per
                    l'utilizzo dei sensori peso.
                    <br />
                    <br />
                    Questa procedura deve essere eseguita per ogni letto su cui
                    si istalla il sistema, e va ripetuta ogni volta che si
                    verifichino cambi di pazienti associati al letto, oppure
                    cambi la fornitura del letto stesso (nuovo materasso, cambio
                    coperte in base alla stagione, etc...).
                    <br />
                    <br />
                    Per il calcolo della tara automatico è richiesto che siano
                    rispettate le seguenti regole:
                  </div>
                  <ul>
                    <li>
                      L'ospite NON deve trovarsi sul letto al momento del
                      calcolo.
                    </li>
                    <li>
                      Sul letto non deve essere presente nulla oltre a lenzuola
                      e coperte: zaini, borse, strumentazioni, o altri oggetti
                      ingombranti che alterino il peso devono essere rimossi.
                    </li>
                    <li>
                      Non sedersi sopra il letto durante il calcolo della tara.
                    </li>
                  </ul>
                  <br />
                  <div>
                    Quando si è pronti, premere il bottone{" "}
                    <strong>Calcola Tara</strong>.
                  </div>
                  <br /> <br />
                </div>
              }
              buttons={[
                {
                  text: "Chiudi",
                  onClick: () => {
                    DismissModal("configureCard-tara-modal");
                  },
                },
                {
                  text: "Calcola Tara",
                  iconName: "Compare",
                  onClick: () => {
                    let peso_1: ISensor | undefined = refSentinel?.sensors.find(
                      (x: ISensor) => x.type === "pesogr"
                    );

                    let peso_2: ISensor | undefined = refSentinel?.sensors.find(
                      (x: ISensor) => x.type === "pesogr2"
                    );

                    if (peso_1 && refSentinel) {
                      peso_1.startThreshold = getRealTimeValue(peso_1);
                      peso_1.threshold = peso_1.startThreshold * 1.5;
                      saveSensorApi(refSentinel.id, peso_1);
                    }
                    if (peso_2 && refSentinel) {
                      peso_2.startThreshold = getRealTimeValue(peso_2);
                      peso_2.threshold = peso_2.startThreshold * 1.5;
                      saveSensorApi(refSentinel.id, peso_2, true);
                    }
                    DismissModal("configureCard-tara-modal");
                  },
                },
              ]}
            />
            {refSentinel.enabled && (
              <div className="auto-tara-row">
                <div>
                  <small>
                    Oltre ai sensori il Letto può disporre di uno o più
                    strumenti associati.
                  </small>
                </div>
                <DefaultButton
                  iconProps={{ iconName: "Settings" }}
                  text="Apri strumenti"
                  onClick={() => {
                    SummonModal("tools-sensor-config-page");
                  }}
                />
              </div>
            )}
          </div>
          {refSentinel.enabled && (
            <div>
              <div className="cf-div">Parametri sensori</div>
              <div className="configure-card-input-sensor-table">
                <div className="configure-card-input-sensor-table-inner">
                  {refSentinel.sensors.length === 0 && (
                    <MessageBar messageBarType={MessageBarType.error}>
                      Errore: il Letto non risulta associato ad alcun sensore.
                    </MessageBar>
                  )}
                  <table style={{ width: "100%" }}>
                    <colgroup>
                      <col style={{ width: 180 }} />
                      <col style={{ width: 130 }} />
                      <col />
                      <col style={{ width: 140 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>Sensore</th>
                        <th>Stato</th>
                        <th>Soglie</th>
                        <th>Abilita sensore</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refSentinel.sensors
                        .filter((x: ISensor) =>
                          ["pesogr", "lidarSx", "lidarDx", "rumore", "door"].includes(
                            x.type
                          )
                        )
                        .map((x: ISensor, i: number) => {
                          return (
                            <tr
                              key={i}
                              className="configure-card-input-sensor-row"
                            >
                              <td>
                                <div className="configure-card-input-sensor-title">
                                  {getSensorIconTooltip(x.type)}
                                </div>
                              </td>
                              <td>
                                <div className="configure-card-sensor-status">
                                  {x.enabled && isSensorOffline(x) && (
                                    <TooltipHost
                                      content={getOfflineTooltipContent(
                                        x,
                                        getSensorIconTooltip(x.type)
                                      )}
                                      id={`sensor_offline_${x.id}`}
                                    >
                                      <span className="cf-pill warn">Offline</span>
                                    </TooltipHost>
                                  )}
                                  {x.enabled && !isSensorOffline(x) && (
                                    <span className="cf-pill ok">Online</span>
                                  )}
                                  {!x.enabled && (
                                    <span className="cf-pill grey">Disabilitato</span>
                                  )}
                                </div>
                              </td>
                              <td>{renderNormalSensor(refSentinel, x)}</td>
                              <td>
                                <div className="configure-card-input-single-button">
                                  <TooltipHost
                                    content={"Abilitazione sensore"}
                                    id={"sentinel_sensor_" + i.toString()}
                                  >
                                    <Checkbox
                                      disabled={!refSentinel.enabled}
                                      checked={x.enabled}
                                      onChange={(
                                        e: any,
                                        checked: boolean | undefined
                                      ) => {
                                        if (checked !== undefined) {
                                          let sentinel: ISentinelRich = {
                                            ...refSentinel,
                                          };
                                          let targetIndex =
                                            sentinel.sensors.findIndex(
                                              (s: ISensor) => s.id === x.id
                                            );
                                          sentinel.sensors[
                                            targetIndex
                                          ].enabled = checked;

                                          setRefSentinel(sentinel);
                                          setEditedSentinel(true);
                                        }
                                      }}
                                    />
                                  </TooltipHost>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {developerUI() && <div>{JSONPrint(refSentinel)}</div>}
    </div>
  );
};

export default ConfigureCard;
